import {noop, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {ContainableSummary, ContainerSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {getAllContainerSchemasFromCache} from 'lookup-config/AllSchemasQueries';
import {EditorPropertiesContext, NoteContext} from 'notebook/notes/core-ui/NoteContext';
import {
  collectSamples,
  STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES,
} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilterType} from 'notebook/sample-propagation/SampleFilter';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import FillPlateFromStructuredTableModal, {
  isNonContainableBioentityColumn,
} from 'samples/plate-maps/fill-from-structured-table-modal/FillPlateFromStructuredTableModal';
import {
  getSourcesFromQueryItems,
  ItemNode,
} from 'samples/plate-maps/fill-plate-modal/FillPlateFromModalUtils';
import FillPlateFromPlannedMaterialInputModal, {
  MaterialInputWithPlannedValueIds,
} from 'samples/plate-maps/fill-plate-modal/FillPlateFromPlannedMaterialInputModal';
import FillPlateFromWorklistModal from 'samples/plate-maps/fill-plate-modal/FillPlateFromWorklistModal';
import ChooseTestDilutionsForSourcesModal, {
  getTestOrders,
} from 'samples/plate-maps/integrations/procedures/ChooseTestDilutionsForSourcesModal';
import {
  getProcedureTestOrderConfig,
  ProcedureTestOrderConfig,
} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import {
  ContainerSource,
  PlateMapProcedureContext,
  ReagentType,
  Source,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {isVolumeOrNullUnits, validateContainerForFill} from 'samples/plate-maps/PlateMapUtil';
import {
  getDefaultMeasurement,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {getAllListingTagSchemasFromCache} from 'schemas/util/getTagSchemasFromCache';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {FilterKey, GLOBAL_SEARCH_URL, keyToEmptyFilter, SearchContext, SortKey} from 'search/SearchConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ChipSearchInput, {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {TextInputType} from 'ui-library/inputs/InputConstants';
import ItemOptionContent from 'ui-library/inputs/ItemOptionContent';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import OptionContent from 'ui-library/menus/OptionContent';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';

// Exported for testing.
export const FETCH_PARAMS = {
  offset: 0,
  limit: 10,
  sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
  context: SearchContext.FOR_WRITE_ACTION,
};

enum FillPlateSentinel {
  ENTITIES = 'ENTITIES',
  CONTAINERS = 'CONTAINERS',
}

const ENTITIES_HEADER_SENTINEL: OptionData = {
  name: FillPlateSentinel.ENTITIES,
  value: FillPlateSentinel.ENTITIES,
  disabled: true,
};

const CONTAINERS_HEADER_SENTINEL: OptionData = {
  name: FillPlateSentinel.CONTAINERS,
  value: FillPlateSentinel.CONTAINERS,
  disabled: true,
};

interface BioentityOptionDataValue {
  item: BioentitySummary;
  name: string;
  type: ModelTypeConstants;
  id: string;
}

interface ContainerSummaryWithContainedItems extends ContainerSummary {
  containedItems: Array<ContainableSummary>;
  containedItemIds: Array<string>;
  inaccessibleContentIds: Array<string>;
  containableConcentrations: {[id: string]: LegacyMeasurement};
}

interface ContainerOptionDataValue {
  item: ContainerSummaryWithContainedItems;
  name: string;
  type: ModelTypeConstants;
  id: string;
}

type FillPlateSearchOptionDataValue = BioentityOptionDataValue | ContainerOptionDataValue;

function isContainerOptionDataValue(
  value: FillPlateSearchOptionDataValue
): value is ContainerOptionDataValue {
  return APIUtil.isContainer(value.id);
}

function isContainableSummaryBioentitySummary(
  containableSummary: ContainableSummary
): containableSummary is BioentitySummary {
  return containableSummary.hasOwnProperty('tagSchema');
}

interface FillPlateSearchOptionData extends OptionData {
  value?: FillPlateSearchOptionDataValue;
}

/**
 * @returns resulting source items after choosing test dilutions, or null if the user cancels the modal
 */
async function maybeChooseTestDilutionsForItems<TItem extends ExcludeInaccessible<ItemNode>>(
  sourceItems: Array<TItem>,
  procedureTestOrderConfig: ProcedureTestOrderConfig | null,
  plateMapType: PlateMapType
): Promise<Array<TItem> | null> {
  if (
    procedureTestOrderConfig == null ||
    sourceItems.every((sourceItem) => getTestOrders(sourceItem).length === 0) // Nothing to choose from
  ) {
    return sourceItems;
  }
  return await runModal(ChooseTestDilutionsForSourcesModal<TItem>, {
    sourceItems,
    procedureTestOrderConfig,
    plateMapType,
  });
}

function FillPlateSearchInputDefaultDropdown({
  autoFocusOnEmptyQuery,
  setAutoFocusOnEmptyQuery,
  isMenuOpen,
  setIsMenuOpen,
  query,
  setQuery,
  onAddItems,
  destinationPlateId,
  procedureContext,
  plateMapType,
}: {
  autoFocusOnEmptyQuery: boolean;
  setAutoFocusOnEmptyQuery: (value: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  query: string;
  setQuery: (value: string) => void;
  onAddItems: (
    sources: Array<Source>,
    schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}},
    testOrderInfoById: TestOrderInfoById | null
  ) => void;
  destinationPlateId: string | null;
  procedureContext: PlateMapProcedureContext;
  plateMapType: PlateMapType;
}): JSX.Element {
  const {note} = NoteContext.useContext();
  const {fileId} = EditorPropertiesContext.useContext();
  const containableSchemaIds = useMemo(
    () =>
      new Set(
        getAllListingTagSchemasFromCache()
          .filter((schema) => schema.isContainable)
          .map((schema) => schema.id)
      ),
    []
  );
  const sampleSources = useMemo(
    () =>
      collectSamples(note ?? null, fileId ?? '', STRUCTURED_TABLE_TYPES_THAT_MIGHT_HAVE_SAMPLES, {
        preFilter: [
          // Not using STORABLE here because that represents ANY storable - including Box & Location which do
          // not directly contain contents. Wells seem to be considered CONTAINER as well.
          {type: SampleFilterType.CONTAINER},
          // Not filtering out non-containable schemas here because we want to display and disable those tables
          // and/or columns as options in the child dropdown
          {type: SampleFilterType.BIOENTITY},
        ],
      }).filter((samplesSource) => samplesSource.table != null),
    [note, fileId]
  );
  const usableSampleSourceIds = useMemo(
    () =>
      new Set(
        sampleSources
          .filter((samplesSource) =>
            samplesSource.samplesByColumn.some(
              (samplesByColumn) => !isNonContainableBioentityColumn(containableSchemaIds, samplesByColumn)
            )
          )
          .map((samplesSource) => samplesSource.table!.provenanceUuid)
      ),
    [containableSchemaIds, sampleSources]
  );

  const materialInputsWithPlannedValues: Array<MaterialInputWithPlannedValueIds> = useMemo(() => {
    const materialInputs: Array<MaterialInputWithPlannedValueIds> = [];
    procedureContext.plateMaterialInputs?.forEach((materialInput) => {
      const plannedValueIds: Array<string> = [];
      materialInput.plannedValues.forEach((value) => {
        if (value.targetFiles != null && value.targetFiles.length > 0) {
          // Material input schemas are always single valued, so the planned value will have just one file.
          plannedValueIds.push(value.targetFiles[0].id);
        }
      });
      if (plannedValueIds.length > 0) {
        materialInputs.push({materialInput, plannedValueIds});
      }
    });
    return materialInputs;
  }, [procedureContext.plateMaterialInputs]);

  const procedureTestOrderConfig = getProcedureTestOrderConfig({
    procedureStudyDesignType: procedureContext.procedureStudyDesignType,
    procedureId: procedureContext.procedureId,
    baseTestOrderConfig: procedureContext.procedureTestOrderConfig,
    procedureMethodExecutionInstanceId: procedureContext.procedureMethodExecutionInstanceId,
  });

  async function handleFillPlateFromStructuredTable(): Promise<void> {
    const result = await runModal(FillPlateFromStructuredTableModal, {
      containableSchemaIds,
      destinationPlateId,
      sampleSources,
      usableSampleSourceIds,
      procedureTestOrderConfig,
      plateMapType,
    });
    if (result == null) {
      return;
    }
    const items = await maybeChooseTestDilutionsForItems(result, procedureTestOrderConfig, plateMapType);
    if (items == null) {
      return;
    }
    const {sources, schemaDataById, testOrderInfoById} = getSourcesFromQueryItems({
      items,
      procedureTestOrderConfig,
      plateMapType,
    });
    onAddItems(sources, schemaDataById, testOrderInfoById);
  }

  async function handleFillPlateFromWorklist(): Promise<void> {
    const result = await runModal(FillPlateFromWorklistModal, {
      destinationPlateId,
      procedureTestOrderConfig,
      plateMapType,
    });

    if (result == null) {
      return;
    }
    const items = await maybeChooseTestDilutionsForItems(
      result.selectedWorklistItems,
      procedureTestOrderConfig,
      plateMapType
    );
    if (items == null) {
      return;
    }
    const {sources, schemaDataById, testOrderInfoById} = getSourcesFromQueryItems({
      items,
      procedureTestOrderConfig,
      plateMapType,
    });
    onAddItems(sources, schemaDataById, testOrderInfoById);
  }

  async function handleFillPlateFromPlannedMaterialInput(): Promise<void> {
    const result = await runModal(FillPlateFromPlannedMaterialInputModal, {
      destinationPlateId,
      materialInputsWithPlannedValues,
      procedureTestOrderConfig,
      plateMapType,
    });
    if (result == null) {
      return;
    }
    const items = await maybeChooseTestDilutionsForItems(
      result.selectedItems,
      procedureTestOrderConfig,
      plateMapType
    );
    if (items == null) {
      return;
    }
    const {sources, schemaDataById, testOrderInfoById} = getSourcesFromQueryItems({
      items,
      materialInputId: result.materialInputId,
      procedureTestOrderConfig,
      plateMapType,
    });
    onAddItems(sources, schemaDataById, testOrderInfoById);
  }

  return (
    <Dropdown
      isOpen={isMenuOpen}
      {...getTestProps({component: 'FillPlateSearchInput', element: 'dropdownToggle'})}
      menu={
        <Menu width={Menu.WIDTHS.XLARGE} height={Menu.HEIGHTS.MEDIUM}>
          <Option isDisabled={true}>Type to search</Option>
          {note != null && fileId != null && (
            <Option
              isDisabled={usableSampleSourceIds.size === 0}
              tooltipTitle={
                usableSampleSourceIds.size === 0
                  ? 'No tables contain entities that can be used in the plate'
                  : null
              }
              icon="note-table"
              onSelect={() => {
                if (usableSampleSourceIds.size > 0) {
                  handleFillPlateFromStructuredTable();
                }
              }}
            >
              Add from structured table
            </Option>
          )}
          <Option
            icon="worklist-add"
            onSelect={handleFillPlateFromWorklist}
            dataTestAttrs={{
              component: 'FillPlateSearchInput',
              element: 'dropdownOption',
              key: 'worklist',
            }}
          >
            Add from worklist
          </Option>
          {materialInputsWithPlannedValues.length > 0 && (
            <Option
              icon="beaker"
              onSelect={handleFillPlateFromPlannedMaterialInput}
              dataTestAttrs={{
                component: 'FillPlateSearchInput',
                element: 'dropdownOption',
                key: 'plannedMaterialInput',
              }}
            >
              Add from planned material inputs
            </Option>
          )}
        </Menu>
      }
    >
      <TextInput
        autoFocus={autoFocusOnEmptyQuery}
        type={TextInputType.TEXT}
        className={css(styles.input)}
        onBlur={() => {
          setIsMenuOpen(false);
        }}
        onChange={({target: {value}}) => {
          setIsMenuOpen(false);
          setAutoFocusOnEmptyQuery(true);
          setQuery(value);
        }}
        onFocus={() => {
          setIsMenuOpen(true);
        }}
        placeholder="Search for entities and containers"
        value={query}
        dataTestAttrs={{component: 'FillPlateSearchInput', element: 'searchInput'}}
      />
    </Dropdown>
  );
}

function getOptionDatasFromResponse(
  response: GlobalSearchResponse,
  destinationPlateId: string | null
): Array<FillPlateSearchOptionData> {
  const [sortedBioentityIds, sortedContainerIds] = partition(response.ids, (id) =>
    APIUtil.isBiologicalEntity(id)
  );
  const bioentityOptionDatas = sortedBioentityIds.map((id) => {
    const bioentitySummary = response.fileSummariesById[id];
    const warning = isArchived(bioentitySummary) ? 'This entity is archived' : null;
    return {
      name: bioentitySummary.name,
      // These entities are necessarily schematized because we are searching with a schema filter
      icon: bioentitySummary.tagSchema!.iconId,
      value: {item: bioentitySummary, name: bioentitySummary.name, type: APIUtil.apiIdToType(id), id},
      ...(warning != null ? {disabledMessage: warning, disabled: true} : {}),
    };
  });
  const containerOptionDatas = sortedContainerIds.map((id) => {
    const containerSummary = response.storableSummariesById[id] as ContainerSummary;
    const containedItems = response.storableSummaryExtras.containedItemsByContainerId?.[id] ?? [];
    const containerSummaryWithContainedItems = {
      ...containerSummary,
      containedItems,
      containedItemIds: containedItems.map(({id}) => id),
      // There probably _are_ containers with inaccessible items but they're explicitly, manually filtered out
      // of global search, so we check after selection of the item.
      inaccessibleContentIds: [] as Array<string>,
      containableConcentrations:
        response.storableSummaryExtras.containableConcentrationsByContainerId?.[id] ?? {},
      permissions: response.permissionsByModelId[id],
    };
    // Inaccessible and archived contents are omitted from search results, so this validation could return
    // a different response than via the call in FillPlateFromWorklistModal, which leverages graphql
    let warning = validateContainerForFill(containerSummaryWithContainedItems, destinationPlateId, false);
    if (!warning && !isVolumeOrNullUnits(containerSummaryWithContainedItems.quantity.units)) {
      warning = 'This container holds solid contents';
    }
    return {
      name: containerSummary.name,
      icon: containerSummary.storableSchema.iconId,
      ...(warning != null ? {disabledMessage: warning, disabled: true} : {}),
      value: {
        item: containerSummaryWithContainedItems,
        name: containerSummary.name,
        type: APIUtil.apiIdToType(id),
        id,
      },
    };
  });
  return [
    ...(bioentityOptionDatas.length > 0 ? [ENTITIES_HEADER_SENTINEL] : []),
    ...bioentityOptionDatas,
    ...(containerOptionDatas.length > 0 ? [CONTAINERS_HEADER_SENTINEL] : []),
    ...containerOptionDatas,
  ];
}

function ValidationErrorIcon({errorText}: {errorText: string}): JSX.Element {
  return (
    <TooltipWrapper tooltipPlacement={TooltipPlacement.TOP} tooltipTitle={errorText}>
      <BenchlingIcon icon="exclamation-circle" className={css(styles.error)} />
    </TooltipWrapper>
  );
}

function FillPlateSearchInputInner({
  onRemove,
  onSelect,
  query,
  setQuery,
  selectedApiIds,
  destinationPlateId,
}: {
  onRemove: (id: string) => void;
  onSelect: (
    reagent: Source,
    schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}}
  ) => void;
  query: string;
  setQuery: (value: string) => void;
  selectedApiIds: Array<string>;
  destinationPlateId: string | null;
}): JSX.Element {
  const containableSchemas = getAllListingTagSchemasFromCache().filter((schema) => schema.isContainable);
  const containableSchemaIds = containableSchemas.map((schema) => schema.id);
  const containerSchemas = getAllContainerSchemasFromCache();
  const containerSchemaIds = containerSchemas.map((schema) => schema.id);

  return (
    <ChipSearchInput
      autoFocus={true}
      chipClass={BenchChip}
      className={css(styles.input)}
      doSearch={searchPost}
      fetchParams={{
        ...FETCH_PARAMS,
        filters: [
          {
            ...keyToEmptyFilter(FilterKey.TYPES),
            value: [...getRegistrySupportedOligoAwareTypes(), ModelTypeConstants.CONTAINER],
          },
          {
            ...keyToEmptyFilter(FilterKey.SCHEMA),
            value: [...containableSchemaIds, ...containerSchemaIds],
          },
        ],
      }}
      getChipProps={(optionValue) => ({
        id: optionValue.id,
        isLink: false,
        size: ChipSize.MINI,
      })}
      getOptionDatasFromResponse={(response) => getOptionDatasFromResponse(response, destinationPlateId)}
      hideChips={true}
      isMulti={true}
      isValueEqual={(firstValue?: OptionValue, secondValue?: OptionValue) =>
        firstValue?.id === secondValue?.id
      }
      onQueryChange={(e) => {
        setQuery(e.target.value);
      }}
      onRemove={(value: FillPlateSearchOptionDataValue) => {
        onRemove(value.id);
      }}
      onSelect={(value: FillPlateSearchOptionDataValue) => {
        let reagent: Source;
        const schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}} = {};
        if (isContainerOptionDataValue(value)) {
          reagent = {
            type: ReagentType.SOURCE_CONTAINER,
            id: value.id,
            groupId: value.item.storableSchema.id,
            contents: [],
            transferQuantity: getDefaultMeasurementWithErrors(),
            // Default is overwritten in FillPlateReagentSettingsPane (if applicable) to allow fetching
            // Unit objects via GQL
            quantity: getDefaultMeasurement(),
          } as ContainerSource;
          if (value.item.parentStorableId != null && APIUtil.isPlate(value.item.parentStorableId)) {
            reagent.parentPlateId = value.item.parentStorableId;
          }
          if (value.item.containedItems.length === 0) {
            // If there's nothing in the container, keep track of the container schema
            schemaDataById[value.item.storableSchema.id] = {
              name: value.item.storableSchema.name,
              iconId: value.item.storableSchema.iconId,
            };
          } else {
            // If there are one or more contents in the container, add them as reagents and keep track of
            // their schemas
            for (const content of value.item.containedItems) {
              assert(isContainableSummaryBioentitySummary(content), 'Contained sources must not be batches');
              assert(content.tagSchema != null, 'Contained sources must have tag schemas');
              reagent.contents.push({
                type: ReagentType.SOURCE_CONTAINABLE,
                id: content.id,
                groupId: content.tagSchema.id,
                transferQuantity: getDefaultMeasurementWithErrors(),
                // Default is overwritten in FillPlateReagentSettingsPane (if applicable) to allow fetching
                // Unit objects via GQL
                sourceConcentration: getDefaultMeasurementWithErrors(),
                finalConcentration: getDefaultMeasurementWithErrors(),
              });
              schemaDataById[content.tagSchema.id] = {
                name: content.tagSchema.name,
                iconId: content.tagSchema.iconId,
              };
            }
          }
        } else {
          assert(value.item.tagSchema != null, 'Containable source must have a tag schema');
          reagent = {
            type: ReagentType.SOURCE_CONTAINABLE,
            id: value.id,
            groupId: value.item.tagSchema.id,
            transferQuantity: getDefaultMeasurementWithErrors(),
            sourceConcentration: getDefaultMeasurementWithErrors(),
            finalConcentration: getDefaultMeasurementWithErrors(),
          };
          schemaDataById[value.item.tagSchema.id] = {
            name: value.item.tagSchema.name,
            iconId: value.item.tagSchema.iconId,
          };
        }
        onSelect(reagent, schemaDataById);
      }}
      query={query}
      placeholder=""
      renderOptionContent={(optionData) => {
        if ((Object.values(FillPlateSentinel) as Array<string>).includes(optionData.value as string)) {
          return <OptionContent>{optionData.name}</OptionContent>;
        }
        return (
          <ItemOptionContent
            optionData={optionData}
            // Don't use the standard tooltip if we have an error tooltip to show.
            tooltip={optionData.disabledMessage != null ? {hideTooltip: true} : null}
            iconOverride={
              optionData.disabled ? <ValidationErrorIcon errorText={optionData.disabledMessage!} /> : null
            }
          />
        );
      }}
      searchOnEmptyQuery={false}
      searchURL={GLOBAL_SEARCH_URL}
      value={selectedApiIds.map((id) => ({id}))}
    />
  );
}

export interface FillPlateSearchInputProps {
  /** A function to be called when selecting or deselecting a single search result */
  onSingleSelect: (
    reagent: Source,
    schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}}
  ) => void;
  /** A function to be called when adding multiple items from worklist */
  onBulkAdd: (
    sources: Array<Source>,
    schemaDataById: {[id: string]: {name: string; iconId: BenchlingIconName}},
    testOrderInfoById: TestOrderInfoById
  ) => void;
  /**
   * The list of API ids of the top-level reagents - containers and lone bioentities - that are currently
   * selected.
   */
  selectedApiIds: Array<string>;
  /** The ID of the plate begin filled */
  destinationPlateId: string | null;
  procedureContext: PlateMapProcedureContext;
  plateMapType: PlateMapType;
}

/**
 * A search bar that looks for containers and containables.
 */
export default function FillPlateSearchInput({
  onSingleSelect,
  onBulkAdd,
  selectedApiIds,
  destinationPlateId,
  procedureContext,
  plateMapType,
}: FillPlateSearchInputProps): JSX.Element {
  // Before a user searches in the search bar, we don't want to autofocus; but after they've typed something,
  // we want to autofocus. If they delete their entire query then we want the dropdown to open again.
  const [autoFocusOnEmptyQuery, setAutoFocusOnEmptyQuery] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [query, setQuery] = useState('');

  if (query.length === 0) {
    return (
      <FillPlateSearchInputDefaultDropdown
        autoFocusOnEmptyQuery={autoFocusOnEmptyQuery}
        setAutoFocusOnEmptyQuery={setAutoFocusOnEmptyQuery}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        query={query}
        setQuery={setQuery}
        onAddItems={onBulkAdd}
        destinationPlateId={destinationPlateId}
        procedureContext={procedureContext}
        plateMapType={plateMapType}
      />
    );
  }
  return (
    <FillPlateSearchInputInner
      onRemove={noop}
      onSelect={onSingleSelect}
      query={query}
      setQuery={setQuery}
      selectedApiIds={selectedApiIds}
      destinationPlateId={destinationPlateId}
    />
  );
}

const styles = StyleSheet.create({
  input: {
    flex: 1,
  },
  error: {
    color: Colors.RED,
  },
});
